import { useMemo } from "react";

import { sortBy } from "lodash";

import { useUser } from "@components/user-context";
import {
  useSalesV4Context,
  useTableRowFilters,
} from "@features/sales-v4/hooks";
import { type TableState } from "@features/sales-v4/types";
import { selectedTabToLevel } from "@features/sales-v4/utils";
import { CONVERSION_LAG_REPORT_V4 } from "@gql/sales-page-v4";
import { useNorthbeamQuery } from "@hooks/http";
import { useUserAttributionSettings } from "@hooks/use-user-attribution-settings";
import {
  type GetConversionLagReportV4,
  type GetConversionLagReportV4Variables,
} from "@north-beam/nb-client-side";
import { type SalesMetricV2 } from "@north-beam/nb-client-side/metrics-v2";
import { formatting, type SalesMetricRow } from "@north-beam/nb-common";

interface Props {
  row: SalesMetricRow;
  tableState: TableState;
  metric: SalesMetricV2;
}

export const usePerformanceData = ({ row, tableState, metric }: Props) => {
  const { user } = useUser();
  const { attributionModel, attributionWindow, accountingMode } =
    useUserAttributionSettings();
  const { dateRangeInterval } = useSalesV4Context();

  const {
    dimensionIds,
    metricFilters,
    breakdownFilters,
    campaignFilters,
    adsetFilters,
    adFilters,
    advancedSearch,
  } = useTableRowFilters({
    tableState,
    row,
  });

  const { data: rawData, loading } = useNorthbeamQuery<
    GetConversionLagReportV4,
    GetConversionLagReportV4Variables
  >(CONVERSION_LAG_REPORT_V4, {
    variables: {
      attributionModel,
      attributionWindow,
      accountingMode,
      dateRange: dateRangeInterval,
      level: selectedTabToLevel(tableState.selectedTab),
      timeGranularity: "daily",
      metricIds: [],
      dimensionIds,
      metricFilters,
      advancedSearch,
      breakdownFilters,
      campaignFilters,
      adsetFilters,
      adFilters,
    },
  });

  const data = useMemo(() => {
    if (!rawData) {
      return [];
    }
    return rawData.me.conversionLagReportV4.conversionLag;
  }, [rawData]);

  const tableData = useMemo(() => {
    const rows = sortBy(
      data.filter((e) =>
        ["1", "30", "infinity", attributionWindow].includes(
          e.attributionWindow,
        ),
      ),
      [(e) => Number(e.attributionWindow)],
    );

    const formatValue = (value: number, format: SalesMetricV2["format"]) => {
      return formatting.formatMetric(value, format, {
        precision: "exact",
        code: user.currencies.currentCurrency,
      });
    };

    const isAllRowsZero = rows.every((row) => {
      let val = metric.getValue(row) ?? 0;
      val = Math.abs(val) < 1e-6 ? 0 : val;
      return val === 0;
    });
    let prevValue = 0;

    return rows.map((row, index) => {
      const value = metric.getValue(row) ?? 0;
      let percentChange = index === 0 ? 1 : (value - prevValue) / prevValue;
      percentChange = Math.abs(percentChange) < 1e-6 ? 0 : percentChange;

      if (isAllRowsZero) {
        percentChange = NaN;
      }

      let formattedPercentChange = formatting.formatPercentage(percentChange, {
        mantissa: 0,
      });

      if (index > 0 && percentChange !== 0 && !isNaN(percentChange)) {
        const sign = percentChange < 0 ? "-" : "+";
        formattedPercentChange = sign + formattedPercentChange;
      }

      prevValue = value;

      return {
        label:
          row.attributionWindow === "infinity"
            ? "Lifetime"
            : `${row.attributionWindow} day`,
        value: formatValue(value, metric.format),
        valueDetails: formattedPercentChange,
      };
    });
  }, [data, attributionWindow, metric]);

  return { data, tableData, loading };
};
